import { Link } from "wouter";
import { ArrowRight, Check, Zap, Shield, Target, Gauge, ChevronDown, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";
import RelatedArticles from "@/components/RelatedArticles";

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx 650, 700)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Specialstål (ballistik, försvar)", maxThickness: "Enligt spec", minThickness: "Enligt spec" },
];

const machines = [
  { name: "Trumpf CO2 Laser 6 kW", spec: "Skäryta 1500 x 3000 mm" },
  { name: "Trumpf CO2 Laser 5 kW", spec: "Skäryta 1500 x 3000 mm" },
  { name: "Amada CO2 Laser 5 kW", spec: "Med automatisk plåtmatning" },
  { name: "Trumpf rörlaser 5 kW", spec: "För profiler och rör" },
];

const localAreas = [
  {
    city: "Eskilstuna",
    href: "/laserskarning-eskilstuna",
    description: "Produktion i vår egen verkstad på Abramsons väg 2.",
    workshop: true,
  },
  {
    city: "Stockholm",
    href: "/laserskarning-stockholm",
    description: "Planerade leveranser till industriföretag i Stockholmsregionen.",
  },
  {
    city: "Västerås",
    href: "/laserskarning-vasteras",
    description: "Regional leverans från Eskilstuna till Västerås med omnejd.",
  },
  {
    city: "Örebro",
    href: "/laserskarning-orebro",
    description: "Planerad leverans från Eskilstuna till Örebroregionen.",
  },
  {
    city: "Södertälje",
    href: "/laserskarning-sodertalje",
    description: "Laserskurna detaljer för industriföretag i Södertälje.",
  },
  {
    city: "Strängnäs",
    href: "/laserskarning-strangnas",
    description: "Närliggande leveransområde med produktion i Eskilstuna.",
  },
  {
    city: "Katrineholm",
    href: "/laserskarning-katrineholm",
    description: "Planerad leverans till Katrineholm med omnejd.",
  },
  {
    city: "Nyköping",
    href: "/laserskarning-nykoping",
    description: "Leveranslösningar till Nyköping och Sörmlandsregionen.",
  },
  {
    city: "Norrköping",
    href: "/laserskarning-norrkoping",
    description: "Planerad leverans till Norrköping och Östergötland.",
  },
  {
    city: "Linköping",
    href: "/laserskarning-linkoping",
    description: "Laserskärning för Linköpings och Östergötlands industri.",
  },
];

export default function Laserskarning() {
  return (
    <Layout>
      <SEOHead
        title="Laserskärning i Eskilstuna – CO2-laser, 0,1 mm | ASPAB"
        description="Professionell laserskärning i stål, rostfritt och aluminium med toleranser ner till 0,1 mm. Snabba leveranser från Eskilstuna. Begär offert!"
        canonical="https://aspab.com/tjanster/laserskarning"
        path="/tjanster/laserskarning"
        breadcrumbs={[
          { name: "Tjänster", href: "/tjanster" },
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
        ]}
              ogImage={IMAGES.ogLaserskarning}
      />

      <PageHero
        title="Laserskärning"
        subtitle="Högsta precision – moderna CO2 Lasrar för perfekt passform. Laserskärning med maximal precision, snabba leveranser och svenskt kvalitetsstål från SSAB som standard."
        breadcrumbs={[
          { name: "Tjänster", href: "/tjanster" },
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">CO2 Laser-teknologi</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Laserskärning med maximal precision och korta leveranstider
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  På ASPAB använder vi moderna lasrar för att leverera högsta kvalitet inom laserskärning.
                  Våra maskiner klarar toleranser ner till 0,1 mm och skär i material som stål, rostfritt,
                  aluminium och höghållfasthetsstål.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  ASPAB är din specialist på laserskärning för industrins mest krävande projekt. Oavsett om
                  det gäller prototyper, försvarsindustrins komponenter eller slitstål för jordbruksmaskiner,
                  levererar vi hög precision med toleranser ner till 0,1 mm och kapacitet upp till 25 mm tjocklek.
                </p>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                    <span className="text-gray-600">Snabb leverans – enkla detaljer skickas ofta samma dag eller dagen efter</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                    <span className="text-gray-600">Svenskt kvalitetsstål från SSAB som standard</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                    <span className="text-gray-600">Expertis inom höghållfasthetsstål, specialstål och ballistiska material</span>
                  </li>
                </ul>
                <p className="text-gray-500 text-sm mt-6">
                  Materialexpertis – vi arbetar med SSAB-stål och andra premiumkvaliteter.
                </p>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Skicka in din ritning idag och få en offert <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">Våra lasermaskiner</h3>
                {machines.map((m) => (
                  <div key={m.name} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
                    <div className="w-2 h-2 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                    <div>
                      <div className="text-gray-900 font-medium">{m.name}</div>
                      <div className="text-gray-500 text-sm">{m.spec}</div>
                    </div>
                  </div>
                ))}
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning hos ASPAB"
                  width={1024}
                  height={1024}
                  className="w-full rounded-lg mt-6"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Varför laser – och varför ASPAB? */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Varför ASPAB</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Varför laser – och varför ASPAB?
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Laserskärning är överlägsen traditionella skärmetoder som plasma och vattenskärning
                  när det gäller precision, hastighet och ytfinish. Våra CO2 Lasrar från Trumpf och Amada
                  representerar marknadens mest beprövade teknik – med hög effekt, stabil strålkvalitet
                  och kapacitet att hantera allt från tunna plåtar till tjockt stål.
                </p>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mt-8 mb-4">Fördelarna med våra CO2 Lasrar:</h3>
                <div className="space-y-4">
                  {[
                    { icon: Zap, title: "Hög effekt", text: "Upp till 6 kW lasereffekt för snabb och effektiv skärning i tjocka material" },
                    { icon: Target, title: "Materialflexibilitet", text: "Perfekt för svart stål, rostfritt, aluminium och höghållfasthetsstål" },
                    { icon: Gauge, title: "Beprövad teknik", text: "Stabil och pålitlig drift med lång livslängd och minimalt stillestånd" },
                    { icon: Shield, title: "Överlägsen skärkvalitet", text: "Exakta snitt, minimal värmepåverkan och slätare ytor" },
                  ].map(({ icon: Icon, title, text }) => (
                    <div key={title} className="flex gap-4">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                        <Icon className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <div>
                        <h4 className="text-gray-900 font-semibold">{title}</h4>
                        <p className="text-gray-500 text-sm mt-0.5">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 font-medium mt-6 italic">
                  Välj ASPAB för precision, kvalitet och snabba leveranser.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="overflow-hidden rounded-lg">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full aspect-video object-cover rounded-lg"
                  poster={IMAGES.heroLaser}
                >
                  <source src={IMAGES.laserVideo} type="video/mp4" />
                </video>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Tekniska Specifikationer */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Tekniska Specifikationer</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Precision i varje detalj
              </h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Vår utrustning är byggd för industrins mest krävande applikationer. Vi skär i en rad
                material med varierande tjocklekar. Alla material levereras med garanterade toleranser
                på 0,1 mm. Vi använder uteslutande SSAB-material om inte kunden förespråkar annat.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Material</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Min tjocklek</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Max tjocklek</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Tolerans</th>
                  </tr>
                </thead>
                <tbody>
                  {capabilities.map((cap) => (
                    <tr key={cap.material} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-gray-900 font-medium">{cap.material}</td>
                      <td className="py-4 px-6 text-gray-500">{cap.minThickness}</td>
                      <td className="py-4 px-6 text-gray-500">{cap.maxThickness}</td>
                      <td className="py-4 px-6 text-[#2563EB] font-medium">0,1 mm</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <p className="text-gray-500 mt-8 leading-relaxed">
              Behöver du laserskurna delar i avancerade material? Kontakta oss för en teknisk genomgång.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Kvalitetssäkring */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <LazyImage
                src={IMAGES.closeup1}
                alt="Laserskuren ståldetalj med hög precision"
                width={1024}
                height={1024}
                className="w-full rounded-lg"
              />
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Kvalitetssäkring</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Från första bit till färdig leverans
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Vi kompromissar aldrig med kvaliteten. Varje detalj kontrolleras noggrant för att
                  säkerställa att den uppfyller våra kunders krav.
                </p>
                <div className="mt-6 space-y-4">
                  {[
                    "Första bit-kontroll vid projektstart",
                    "Löpande stickprov under produktion",
                    "Digital mätteknik för att garantera snäva toleranser",
                    "Direkt åtgärd vid avvikelser för att säkerställa kontinuerlig kvalitet",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                      <span className="text-gray-600">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 font-medium mt-6 italic">
                  Med Aspab får du garanterad precision – varje gång.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Avgradning – Lissmac */}
      <section className="bg-white py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Avgradning och ytfinish</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Gradfria kanter direkt efter laserskärning
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Laserskurna plåtar kan ibland få vassa kanter på undersidan – så kallade grader. Med vår
                  nya Lissmac SBM-L 1500 G1S2 gradningsmaskin bearbetar vi båda sidor av plåten i ett enda
                  genomdrag. Resultatet är jämna, hanteringssäkra kanter utan manuellt efterarbete.
                </p>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                    <span className="text-gray-600">Automatisk avgradning och kantbrytning i ett steg</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                    <span className="text-gray-600">Upp till 60 % kortare processtid jämfört med manuell avgradning</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                    <span className="text-gray-600">Materialtjocklek 0,5–50 mm, arbetsbredd 1 500 mm</span>
                  </li>
                </ul>
                <Link
                  href="/nyheter/lissmac-gradningsmaskin"
                  className="inline-flex items-center gap-2 text-[#2563EB] font-medium hover:text-[#1D4ED8] transition-colors mt-6"
                >
                  Läs mer om vår Lissmac-investering <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.lissmacMaskin}
                alt="Lissmac SBM-L 1500 G1S2 gradningsmaskin hos ASPAB"
                width={1536}
                height={1536}
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Arbetsprocess</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Från ritning till färdig detalj
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Offertförfrågan", desc: "Skicka in din ritning eller beskriv vad du behöver. Vi tar emot CAD-filer, PDF:er och till och med skisser." },
              { step: "02", title: "Rådgivning", desc: "Vi granskar underlaget och ger råd om materialval, design och optimering för bästa resultat och kostnad." },
              { step: "03", title: "Produktion", desc: "Skärning med CO2 Laser, med första bit-kontroll och stickprov under hela produktionen." },
              { step: "04", title: "Leverans", desc: "Enkla bitar samma dag eller dagen efter. Komplexa projekt med bockning och svetsning inom två dagar." },
            ].map((item, i) => (
              <AnimatedSection key={item.step} delay={i * 120}>
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/20 transition-colors">
                  <div className="font-heading text-3xl font-bold text-[#2563EB]/20">{item.step}</div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Leveransområden — lokal hubb för samtliga ortssidor */}
      <section
        id="leveransomraden"
        className="scroll-mt-24 border-t border-gray-200 bg-gradient-to-b from-slate-50 to-white py-20 md:py-28"
      >
        <div className="container">
          <AnimatedSection>
            <div className="mb-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
              <div className="max-w-2xl">
                <span className="text-sm font-medium uppercase tracking-wider text-[#2563EB]">
                  Laserskärning nära dig
                </span>
                <h2 className="mt-3 font-heading text-3xl font-bold text-gray-950 md:text-4xl">
                  Produktion i Eskilstuna – leverans till din region
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-gray-600">
                  Välj din ort för lokal information om laserskärning, kapacitet och leverans.
                  Alla detaljer produceras av ASPAB i Eskilstuna och transportupplägget bekräftas i offerten.
                </p>
              </div>
              <div className="inline-flex w-fit items-center gap-3 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-[#2563EB]">
                  <MapPin className="h-4 w-4" />
                </span>
                10 lokala leveransområden
              </div>
            </div>
          </AnimatedSection>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {localAreas.map((area, index) => (
              <AnimatedSection key={area.href} delay={(index % 5) * 70}>
                <Link
                  href={area.href}
                  className={`group flex h-full min-h-48 flex-col rounded-xl border p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#2563EB]/40 hover:shadow-xl hover:shadow-blue-950/10 ${
                    area.workshop
                      ? "border-blue-200 bg-blue-50/70"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
                        area.workshop
                          ? "bg-[#2563EB] text-white"
                          : "bg-gray-100 text-[#2563EB] group-hover:bg-blue-50"
                      }`}
                    >
                      <MapPin className="h-5 w-5" />
                    </span>
                    {area.workshop && (
                      <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#2563EB] shadow-sm">
                        Vår verkstad
                      </span>
                    )}
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-gray-950 transition-colors group-hover:text-[#2563EB]">
                    Laserskärning {area.city}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-500">
                    {area.description}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB]">
                    Se lokal information
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16 md:py-20 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                Vanliga frågor om laserskärning
              </h2>
              <div className="space-y-4">
                {[
                  { q: "Vilka material kan ni laserskära?", a: "Vi skär svart stål (upp till 25 mm), rostfritt stål (upp till 20 mm), aluminium (upp till 15 mm), höghållfasthetsstål som Strenx 650/700 samt slitstål som Hardox och Raex. Vi hanterar även specialstål för försvar och jordbruk." },
                  { q: "Vilka toleranser kan ni uppnå?", a: "Vi garanterar toleranser ner till 0,1 mm. Varje projekt inleds med en första bit-kontroll för att säkerställa att alla mått stämmer innan serieproduktion påbörjas." },
                  { q: "Hur snabbt kan ni leverera laserskurna detaljer?", a: "Enkla detaljer kan ofta levereras samma dag eller dagen efter. Mer komplexa projekt levereras normalt inom två arbetsdagar beroende på volym och komplexitet." },
                  { q: "Vilken typ av lasrar använder ni?", a: "Vi har tre moderna CO2-lasrar från Trumpf och Amada med upp till 6 kW effekt, samt en Trumpf rörlaser på 5 kW för profiler och rör." },
                  { q: "Kan ni hantera både prototyper och stora serier?", a: "Ja, vi hanterar allt från enstaka prototyper till stora serier. Vid volymer över 20 bitar sjunker styckpriset tack vare fördelade ställtider." },
                  { q: "Behöver jag skicka CAD-filer?", a: "Vi tar emot allt från färdiga CAD-filer (DXF, DWG, STEP) till enkla skisser. Vi kan även hjälpa till med konstruktion och ge råd om materialval och designoptimering." },
                ].map((faq, i) => (
                  <details key={i} className="group border border-gray-200 rounded-lg">
                    <summary className="flex items-center justify-between cursor-pointer p-5 text-gray-900 font-medium hover:bg-gray-50 transition-colors">
                      {faq.q}
                      <ChevronDown className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="px-5 pb-5 text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Kunskapsbank */}
      <RelatedArticles
        heading="Lär dig mer om laserskärning"
        subheading="Fördjupa dig i teknik, material och bästa praxis inom laserskärning med våra expertartiklar."
        articles={[
          { title: "Laserskärning vs plasmaskärning", slug: "laserskarning-vs-plasmaskaring", excerpt: "Vilken skärmetod passar bäst? Vi jämför precision, kostnad och materialkapacitet." },
          { title: "Skärgaser vid laserskärning", slug: "laserskarning-vs-plasmaskaring", excerpt: "Hur valet av skärgas påverkar skärkvalitet, hastighet och materialkostnad." },
          { title: "Laserskärning av rostfritt stål", slug: "laserskarning-rostfritt-stal", excerpt: "Tekniska utmaningar och lösningar vid skärning av rostfritt stål." },
          { title: "Så förbereder du CAD-filer", slug: "sa-forbereder-du-cad-filer-for-laserskarning", excerpt: "Rätt filformat, toleranser och vanliga misstag att undvika." },
          { title: "Materialguide: stål, rostfritt, aluminium", slug: "materialguide-stal-rostfritt-aluminium", excerpt: "Egenskaper, användningsområden och val av rätt material för ditt projekt." },
        ]}
      />
    </Layout>
  );
}
